import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { Alert, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters } from "@/lib/filters";

import { RefreshButton } from "./RefreshButton";
import { getProductionTab, PRODUCTION_QUERY_KEY, type ProductionLine, type ProductionPlan } from "./api";
import {
  PLAN_CHECK, PRODUCTION_VIEWS, convertCases, convertWords, fmtQty, monthLabel, planCountWords, planLink, planQuantity, productLabel,
  produceWords, productionBanner, productionBuckets, productionErrorMessage, productionHorizonLine, productionLines,
  productionNotProducedLine, productionPlans, productionUnsizedLine, productionView, dayLabel,
} from "./format";
import { useRefresh } from "./useRefresh";

/**
 * T3 Production Plan (REQ-PRD-002 to 005, C6 v1.10): the engine's monthly production need per entity and product,
 * conversion first, beside the open Production COGS plans and how each was counted. Read-only: a plan is made in
 * Production COGS, which the "Plan a production" link opens.
 */
export function ProductionTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const filterKey = JSON.stringify(values);

  const query = useQuery({ queryKey: PRODUCTION_QUERY_KEY, queryFn: getProductionTab });
  const artifact = query.data?.data.artifact ?? null;
  const latestRun = query.data?.data.latest_run ?? null;
  // A manual refresh is a full run, which computes the forecast; the Xero button belongs to the Stock tab.
  const refresh = useRefresh(areaName, latestRun, false);

  const view = productionView(values);
  const lines = useMemo(() => productionLines(artifact?.lines ?? [], values), [artifact, filterKey]);
  const buckets = productionBuckets(lines);
  // Whole cases round on each product's cumulative across every served month, not the shown ones (R1-1).
  const toOpen = useMemo(() => convertCases(artifact?.lines ?? []), [artifact]);
  const plans = useMemo(() => productionPlans(artifact?.plans ?? [], values), [artifact, filterKey]);
  const banner = productionBanner(artifact, latestRun, { stalledRunId: refresh.stalledRunId });
  const horizonLine = productionHorizonLine(artifact);
  const unsizedLine = productionUnsizedLine(artifact);
  const notProducedLine = productionNotProducedLine(artifact);
  const tiles = artifact?.tiles ?? null;

  const filters: FilterSpec[] = [
    { name: "show", label: "Show", kind: "choice", options: PRODUCTION_VIEWS },
    { name: "search", label: "Search product or SKU", kind: "text" },
    {
      name: "entity", label: "Entity", kind: "choice",
      options: (artifact?.filters.entities ?? []).map((e) => ({ value: e, label: e })),
    },
    {
      name: "product", label: "Product", kind: "choice",
      options: (artifact?.filters.products ?? []).map((p) => ({ value: p, label: p })),
    },
    {
      name: "month", label: "Month", kind: "choice",
      options: (artifact?.months ?? []).map((m) => ({ value: m, label: monthLabel(m) })),
    },
  ];

  const lineColumns: ColumnDef<ProductionLine, unknown>[] = [
    { id: "entity", header: "Entity", cell: ({ row }) => row.original.entity_id },
    { id: "product", header: "Product", cell: ({ row }) => <span className="font-medium">{productLabel(row.original)}</span> },
    { id: "need", header: "Still needed", cell: ({ row }) => `${row.original.need_units.toLocaleString("en-GB")} units` },
    {
      id: "action", header: "Convert, then produce",
      cell: ({ row }) => {
        const steps = [convertWords(row.original, toOpen.get(row.original.forecast_line_id)), produceWords(row.original)].filter(Boolean);
        return steps.length ? steps.map((s) => <span key={s} className="block">{s}</span>) : "Covered by stock and plans";
      },
    },
    { id: "plans", header: "Plans arriving", cell: ({ row }) => (Number(row.original.plan_units) ? `${fmtQty(row.original.plan_units)} units` : "") },
    {
      id: "plan", header: "",
      cell: ({ row }) => row.original.qty > 0
        ? <Link {...planLink(row.original.entity_id)} className="text-sm text-ks-ink underline-offset-2 hover:underline">Plan a production</Link>
        : null,
    },
  ];

  const planColumns: ColumnDef<ProductionPlan, unknown>[] = [
    { id: "key", header: "Production", cell: ({ row }) => <span className="font-medium">{row.original.production_key}</span> },
    { id: "entity", header: "Entity", cell: ({ row }) => row.original.entity_id },
    { id: "sku", header: "SKU", cell: ({ row }) => row.original.sku },
    { id: "product", header: "Product", cell: ({ row }) => productLabel(row.original) },
    { id: "qty", header: "Quantity", cell: ({ row }) => planQuantity(row.original) },
    { id: "status", header: "Status", cell: ({ row }) => row.original.status },
    { id: "factory", header: "Factory confirmed", cell: ({ row }) => (row.original.confirmed_with_factory ? "Yes" : "No") },
    { id: "target", header: "Target date", cell: ({ row }) => dayLabel(row.original.target_date) ?? "none" },
    {
      id: "counts", header: "How the forecast counts it",
      cell: ({ row }) => (
        <span>
          {planCountWords(row.original)}
          {PLAN_CHECK.includes(row.original.counts_as) ? <Badge variant="destructive" className="ml-2">CHECK</Badge> : null}
        </span>
      ),
    },
  ];

  const emptyLines = !artifact
    ? "No production plan has been computed yet."
    : view === "produce"
      ? "Nothing to produce in the six months beyond the open plans."
      : "No lines match these filters.";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {banner.kind !== "ok" ? (
          <Alert variant={banner.kind === "failed" ? "destructive" : "default"} className="max-w-3xl">
            <AlertTitle>{banner.text}</AlertTitle>
          </Alert>
        ) : <div />}
        <RefreshButton
          state={refresh.state}
          message={refresh.message}
          progress={refresh.progress}
          xeroAvailable={false}
          onStart={(source) => void refresh.start(source)}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {[
          ["Open productions", tiles?.open_productions ?? ""],
          ["In production", tiles?.in_production ?? ""],
          ["Product-months to produce, no plan yet", tiles?.needed_no_plan ?? ""],
        ].map(([label, value]) => (
          <Card key={String(label)}>
            <CardHeader className="pb-1"><CardTitle className="text-sm font-normal">{label}</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold">
              {value}
              {/* The tiles count the whole forecast; the filters narrow the tables only. */}
              {tiles ? <span className="block text-xs font-normal text-muted-foreground">all entities</span> : null}
            </CardContent>
          </Card>
        ))}
      </div>

      {horizonLine ? <p className="text-sm text-muted-foreground">{horizonLine}</p> : null}
      {notProducedLine ? <p className="text-sm text-muted-foreground">{notProducedLine}</p> : null}
      {unsizedLine ? <p role="status" className="text-sm text-ks-ink">{unsizedLine}</p> : null}

      <div>
        <FilterBar
          filters={filters}
          values={values}
          onChange={(next) => {
            const navigateOptions: Record<string, unknown> = {
              search: (prev: Record<string, unknown>) => applyFilters(prev, next),
              replace: true,
            };
            void navigate(navigateOptions as never);
          }}
        />
        {query.isError ? (
          <p role="alert" className="text-sm text-ks-danger">{productionErrorMessage(query.error, areaName)}</p>
        ) : (
          <DataTable
            columns={lineColumns}
            groups={buckets}
            page={{ pageIndex: 0, pageSize: Math.max(1, lines.length), rowCount: lines.length }}
            onPageChange={() => {}}
            pending={query.isLoading}
            emptyMessage={emptyLines}
          />
        )}
        <p className="mt-2 text-sm text-muted-foreground">
          Each month shows what is still needed after stock and the open plans: convert cases first, then produce whole cases.
        </p>
      </div>

      {artifact ? (
        <div>
          <h3 className="mb-2 text-base font-semibold">Open productions in Production COGS</h3>
          <DataTable
            columns={planColumns}
            rows={plans}
            page={{ pageIndex: 0, pageSize: Math.max(1, plans.length), rowCount: plans.length }}
            onPageChange={() => {}}
            emptyMessage="No open production in Production COGS."
          />
          <p className="mt-2 text-sm text-muted-foreground">
            Plans are made and changed in Production COGS; this tab only reads them.
          </p>
        </div>
      ) : null}
    </div>
  );
}
